import { ScrollDispatcher, ScrollingModule } from '@angular/cdk/scrolling';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, isDevMode, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import player from 'lottie-web';
import { TagInputModule } from 'ngx-chips';
import { provideLottieOptions } from 'ngx-lottie';
import { CalendarModule } from 'primeng/calendar';
import { InputNumberModule } from 'primeng/inputnumber';
import { PaginatorModule } from 'primeng/paginator';
import { ProgressBarModule } from 'primeng/progressbar';
import { SliderModule } from 'primeng/slider';
import { first, Observable, tap } from 'rxjs';
import { Constants } from './_main/helpers/constant';
import { AdminGuard } from './_services/admin-gaurd';
import { AuthService } from './_services/auth.service';
import { AuthInterceptor } from './_services/authInterceptor';
import { ConfigService } from './_services/config.service';
import { CustomerGuard } from './_services/customer-gaurd';
import { SupplierGuard } from './_services/supplier-gaurd';
import { CommonState } from './_state/common.state';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CallbackComponent } from './callback/callback.component';
import { CustomOrderProcessingComponent } from './custom-order-processing/custom-order-processing.component';
import { ErrorComponent } from './error-page/error-page.component';
import { LoaderComponent } from './loader/loader.component';
import { GridState } from './shared/_services/grid.state';
import { EntryState } from './shared/entries-state.state';
import { SpovixAuthScreenComponent } from './shared/spovix-auth-screen/spovix-auth-screen.component';
import { ViefyChangeEmailComponent } from './viefy-change-email/viefy-change-email.component';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MyPreset } from './spovix-preset';
import { definePreset } from '@primeng/themes';
import { AuthConfigModule } from './auth-config.module';
import { AuthState } from './_state/auth.state';
import { RevenueSummaryState } from './supplier/_state/revenue-summary-state';
import { providePrimeNG } from 'primeng/config';

import {
  NgxsStoragePluginModule,
  SESSION_STORAGE_ENGINE,
} from '@ngxs/storage-plugin';
export const spovixTheme = definePreset(MyPreset, {});
export function authFactory(authService: AuthService, store: Store): () => Observable<any> {
  return () =>
    authService.checkAuth().pipe(
      first(),
      tap(({ isAuthenticated, accessToken }) => {
        if (isAuthenticated) {
          localStorage.setItem(Constants.token_key, accessToken);
          authService.setSession();
        } else {
          authService.clearAuthState();
        }
      })
    );
}
@NgModule({
  declarations: [
    AppComponent,
    LoaderComponent,
    ErrorComponent,
    ViefyChangeEmailComponent,
    CustomOrderProcessingComponent,
    CallbackComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    ScrollingModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    TagInputModule,
    SliderModule,
    PaginatorModule,
    InputNumberModule,
    CalendarModule,
    ProgressBarModule,
    SpovixAuthScreenComponent,
    // ServiceWorkerModule.register('ngsw-worker.js', {
    //   enabled: environment.production,
    //   // Register the ServiceWorker as soon as the application is stable
    //   // or after 30 seconds (whichever comes first).
    //   registrationStrategy: 'registerWhenStable:30000',
    // }),
    RxReactiveFormsModule,
    FroalaEditorModule.forRoot(),
    FroalaViewModule.forRoot(),
    NgxsModule.forRoot([GridState, CommonState, EntryState, AuthState]),
    NgxsLoggerPluginModule.forRoot({ disabled: !isDevMode() }),
     NgxsStoragePluginModule.forRoot({
      keys: [
        AuthState,
        RevenueSummaryState,
        // Dashboard filters are kept for the browser session only (DASH-01 AC-006).
        { key: 'dashboard.filter', engine: SESSION_STORAGE_ENGINE },
      ],
    }),
    AuthConfigModule
  ],
  providers: [
    ScrollDispatcher,
    Title,
    AuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: authFactory,
      deps: [AuthService, Store],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    ConfigService,
    provideLottieOptions({
      player: () => player,
    }),
    AdminGuard,
    SupplierGuard,
    CustomerGuard,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: spovixTheme,
        options: {
          darkModeSelector: false || 'none'
      }
      },
    }),
  ],
})
export class AppModule {}
